import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, ChevronDown, ChevronRight, ClipboardList, Loader2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../shared/Toast';
import { API_BASE_URL } from '../config/api';
import ConfirmModal from '../shared/ConfirmModal';
import { DetailPanel, EmptyState, GHOST_BTN, INPUT_CLS, PRIMARY_BTN } from '../shared/listPage';
import { LoadFailed } from '../settings/instantSettings';
import { fmtQty } from './warehouseUi';

/**
 * Φυσική απογραφή (14/10/2026): συρτάρι αντί για παράθυρο σε όλη την οθόνη. Ίδια ροή με πριν: όλες οι
 * πρώτες ύλες ανά κατηγορία, «Πραγματικό» μόνο όπου διαφέρει, επιβεβαίωση, και POST /raw-materials/stock-count
 * με { notes, items: [{ raw_material_id, actual_quantity }] } μόνο για ό,τι άλλαξε.
 */

interface RawMaterial {
  id: number;
  name: string;
  purchase_unit: string;
  current_stock: number;
  min_stock_level: number;
  category_name?: string;
}

interface CountItem {
  material: RawMaterial;
  actual: string; // string so input can be empty
}

interface StockCountModalProps {
  onClose: () => void;
  onComplete: () => void;
}

// Στήλες: πρώτη ύλη | μονάδα | σύστημα | πραγματικό | διαφορά (στο κινητό χωρίς μονάδα).
const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_3.5rem_5.5rem_3.25rem] sm:grid-cols-[minmax(0,1fr)_6rem_5.5rem_7rem_4.5rem] gap-x-2 sm:gap-x-3 items-center';

const StockCountModal: React.FC<StockCountModalProps> = ({ onClose, onComplete }) => {
  const { t } = useTranslation(['warehouse', 'common', 'listPage']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<CountItem[]>([]);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchMaterials();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  useEffect(() => {
    if (!loading && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [loading]);

  const fetchMaterials = async () => {
    if (!store?.id) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      const token = localStorage.getItem('xentrix_token');
      const res = await fetch(`${API_BASE_URL}/api/warehouse/stores/${store.id}/raw-materials`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      const noCategoryLabel = t('warehouse:noCategory');
      const materials: RawMaterial[] = (data.rawMaterials || []).sort((a: RawMaterial, b: RawMaterial) => {
        const catA = a.category_name || noCategoryLabel;
        const catB = b.category_name || noCategoryLabel;
        return catA.localeCompare(catB) || a.name.localeCompare(b.name);
      });
      setItems(materials.map(m => ({ material: m, actual: '' })));
    } catch {
      showToast('error', t('warehouse:loadingError'));
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const setActual = (id: number, value: string) => {
    // Allow only numbers and one dot
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    setItems(prev => prev.map(i => i.material.id === id ? { ...i, actual: value } : i));
  };

  const getDiff = (item: CountItem): number | null => {
    if (item.actual === '') return null;
    const actual = parseFloat(item.actual);
    if (isNaN(actual)) return null;
    return actual - parseFloat(String(item.material.current_stock));
  };

  // Items that have been filled in and differ from system
  const changedItems = items.filter(i => {
    const d = getDiff(i);
    return d !== null && Math.abs(d) >= 0.001;
  });

  // Group by category
  const grouped = items.reduce<Record<string, CountItem[]>>((acc, item) => {
    const cat = item.material.category_name || t('warehouse:noCategory');
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const toggleCategory = (cat: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const handleApply = async () => {
    if (!store?.id) return;
    if (changedItems.length === 0) { onClose(); return; }

    setSaving(true);
    try {
      const token = localStorage.getItem('xentrix_token');
      const payload = {
        notes,
        items: changedItems.map(i => ({
          raw_material_id: i.material.id,
          actual_quantity: parseFloat(i.actual),
        })),
      };
      const res = await fetch(`${API_BASE_URL}/api/warehouse/stores/${store.id}/raw-materials/stock-count`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('save failed');
      const data = await res.json();
      showToast('success', t('warehouse:stockCountComplete', { count: data.adjusted }));
      onComplete();
    } catch {
      showToast('error', t('warehouse:stockCountSaveError'));
    } finally {
      setSaving(false);
    }
  };

  // Esc / X / φόντο / «Ακύρωση»: με μετρήσεις που δεν εφαρμόστηκαν, ερώτηση πρώτα.
  const requestClose = () => {
    if (confirmDiscard) {
      setConfirmDiscard(false);
      return;
    }
    if (saving) return;
    if (changedItems.length > 0 || notes !== '') setConfirmDiscard(true);
    else onClose();
  };

  const diffColor = (diff: number | null) => {
    if (diff === null) return 'text-t4';
    if (Math.abs(diff) < 0.001) return 'text-t4';
    return diff > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400';
  };

  const diffLabel = (item: CountItem) => {
    const d = getDiff(item);
    if (d === null) return '—';
    if (Math.abs(d) < 0.001) return '✓';
    return (d > 0 ? '+' : '') + d.toFixed(3).replace(/\.?0+$/, '');
  };

  let firstInputAssigned = false;

  const increases = changedItems.filter(i => (getDiff(i) ?? 0) > 0).length;
  const decreases = changedItems.filter(i => (getDiff(i) ?? 0) < 0).length;

  const body = () => {
    if (loading) {
      return (
        <div className="space-y-3 pt-1" data-testid="stock-count-loading">
          {[70, 52, 64, 45].map((w, i) => <span key={i} className="lp-skel" style={{ width: `${w}%` }} />)}
          <span className="sr-only" role="status">{t('warehouse:loadingMaterials')}</span>
        </div>
      );
    }
    if (loadFailed) {
      return <LoadFailed message={t('warehouse:loadingError')} onRetry={fetchMaterials} />;
    }
    if (items.length === 0) {
      return <EmptyState icon={ClipboardList} title={t('warehouse:noRawMaterialsRegistered')} />;
    }
    return (
      <div className="space-y-5">
        <div>
          <label htmlFor="stock-count-notes" className="block text-sm font-medium text-t2 mb-1.5">{t('warehouse:ui.stockCount.notes')}</label>
          <input
            id="stock-count-notes"
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={t('warehouse:notePlaceholder')}
            className={INPUT_CLS}
          />
        </div>

        {/* Χωρίς ρόλους πίνακα: οι κατηγορίες είναι κουμπιά που κλείνουν/ανοίγουν και κάθε πεδίο λέει μόνο
            του ποια πρώτη ύλη μετρά (aria-label). Οι επικεφαλίδες είναι για το μάτι. */}
        <div className="text-sm" data-testid="stock-count-list">
          {/* Table header */}
          <div aria-hidden="true" className={`${ROW_GRID} pb-2 border-b border-line-strong text-xs font-semibold text-t3`}>
            <span>{t('warehouse:rawMaterialHeader')}</span>
            <span className="hidden sm:block text-right">{t('warehouse:unitHeader')}</span>
            <span className="text-right">{t('warehouse:systemHeader')}</span>
            <span className="text-right">{t('warehouse:actualHeader')}</span>
            <span className="text-right">{t('warehouse:diffHeader')}</span>
          </div>

          {Object.entries(grouped).map(([category, catItems]) => {
            const isCollapsed = collapsedCategories.has(category);
            return (
              <div key={category}>
                {/* Category header */}
                <div>
                  <button
                    type="button"
                    aria-expanded={!isCollapsed}
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center gap-2 py-2.5 border-b border-line text-left hover:bg-surface-2 transition-colors"
                  >
                    {isCollapsed
                      ? <ChevronRight className="w-4 h-4 text-t3 shrink-0" aria-hidden="true" />
                      : <ChevronDown className="w-4 h-4 text-t3 shrink-0" aria-hidden="true" />}
                    <span className="text-sm font-semibold text-t1">{category}</span>
                    <span className="text-xs text-t3 tabular-nums">({catItems.length})</span>
                  </button>
                </div>

                {/* Items */}
                {!isCollapsed && catItems.map(item => {
                  const diff = getDiff(item);
                  const isRef = !firstInputAssigned;
                  if (isRef) firstInputAssigned = true;
                  const stock = parseFloat(String(item.material.current_stock));
                  const changed = diff !== null && Math.abs(diff) >= 0.001;

                  return (
                    <div
                      key={item.material.id}
                      data-testid={`stock-count-row-${item.material.id}`}
                      className={`${ROW_GRID} py-2 border-b border-line/70 ${changed ? 'bg-brand-soft/40' : ''}`}
                    >
                      <div className="min-w-0 pl-2 sm:pl-6">
                        <span className="block text-t1">{item.material.name}</span>
                        {/* Δεύτερη γραμμή: στο κινητό και η μονάδα (εκεί δεν έχει δική της στήλη). */}
                        <span className="block text-xs text-t4 tabular-nums">
                          <span className="sm:hidden">{item.material.purchase_unit}{item.material.min_stock_level > 0 ? ' · ' : ''}</span>
                          {item.material.min_stock_level > 0 && t('warehouse:ui.minShort', { value: item.material.min_stock_level })}
                        </span>
                      </div>
                      <div className="hidden sm:block text-right text-t3">{item.material.purchase_unit}</div>
                      <div className="text-right">
                        <span className={`tabular-nums ${stock <= 0
                          ? 'text-red-700 dark:text-red-400'
                          : stock <= item.material.min_stock_level
                            ? 'text-amber-800 dark:text-amber-300'
                            : 'text-t2'}`}>
                          {fmtQty(stock)}
                        </span>
                      </div>
                      <div>
                        <input
                          ref={isRef ? firstInputRef : undefined}
                          type="text"
                          inputMode="decimal"
                          value={item.actual}
                          onChange={e => setActual(item.material.id, e.target.value)}
                          placeholder={fmtQty(stock)}
                          aria-label={t('warehouse:ui.stockCount.actualLabel', { name: item.material.name })}
                          className={`w-full h-10 px-2.5 text-right rounded-lg border bg-surface-2 text-t1 placeholder-t4 tabular-nums focus:outline-none focus:ring-2 focus:ring-ring/50 transition-colors ${changed
                            ? diff! > 0 ? 'border-emerald-500' : 'border-red-500'
                            : 'border-line-strong focus:border-ring'}`}
                        />
                      </div>
                      <div className={`text-right font-semibold tabular-nums ${diffColor(diff)}`}>
                        {diffLabel(item)}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <DetailPanel
      open
      onClose={requestClose}
      title={t('warehouse:ui.stockCount.title')}
      subtitle={t('warehouse:physicalStockCountDesc')}
      width="lg"
      footerStart={changedItems.length > 0 ? (
        <div className="flex flex-col gap-1.5 min-w-0" data-testid="stock-count-summary">
          <p className="text-sm text-t2">
            <span className="text-t3">{t('warehouse:changesLabel')}</span>{' '}
            <span className="font-semibold text-t1">{t('warehouse:materialsCountLabel', { count: changedItems.length })}</span>
            {' · '}
            <span className="text-emerald-700 dark:text-emerald-400 tabular-nums">{t('warehouse:increaseLabel', { count: increases })}</span>
            {' · '}
            <span className="text-red-700 dark:text-red-400 tabular-nums">{t('warehouse:decreaseLabel', { count: decreases })}</span>
          </p>
          {/* Confirm checkbox */}
          <label className="flex items-start gap-2 cursor-pointer text-sm text-t2">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              className="mt-0.5 w-4 h-4 shrink-0 accent-[hsl(var(--brand))]"
            />
            <span>{t('warehouse:confirmPhysicalStock')}</span>
          </label>
        </div>
      ) : undefined}
      footer={(
        <>
          <button type="button" className={GHOST_BTN} onClick={requestClose} disabled={saving}>{t('warehouse:cancelButton')}</button>
          <button
            type="button"
            className={PRIMARY_BTN}
            onClick={handleApply}
            disabled={saving || loading || (changedItems.length > 0 && !confirmed)}
            aria-busy={saving || undefined}
          >
            {saving
              ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('warehouse:savingLabel')}</>
              : changedItems.length === 0
                ? <><CheckCircle className="w-4 h-4" aria-hidden="true" /> {t('warehouse:noChanges')}</>
                : <><CheckCircle className="w-4 h-4" aria-hidden="true" /> {t('warehouse:executeStockCount', { count: changedItems.length })}</>}
          </button>
        </>
      )}
      testId="stock-count"
    >
      {body()}
      <ConfirmModal
        isOpen={confirmDiscard}
        title={t('warehouse:ui.stockCount.discardTitle')}
        message={t('warehouse:ui.stockCount.discardMessage')}
        confirmLabel={t('warehouse:ui.discard')}
        cancelLabel={t('warehouse:ui.keepCounting')}
        variant="warning"
        onConfirm={() => { setConfirmDiscard(false); onClose(); }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </DetailPanel>
  );
};

export default StockCountModal;
